import IPostMessage from '../../IPostMessage';
import ISetColorMessage from './ISetColorMessage';
import ErrorCodes from '../../Error/ErrorCodes';
import validate from '../../Validate/Validate';
import ILed from './ILed';

/**
 * The `sos.hardware.led` API groups together methods for controlling LEDs of the device.
 *
 * :::warning
 * This is currently only supported by Phillips devices.
 * :::
 */
export default class Led implements ILed {
	public static MESSAGE_PREFIX: string = 'led';

	/** @internal */
	constructor(
		private messagePrefix: string,
		private postMessage: IPostMessage<ISetColorMessage>,
	) {}

	/**
	 * The `setColor()` methods sets the LED color.
	 *
	 * @param color The color to set the LED to, in hexadecimal format (e.g. `#FF0000` for red). If `null`, it will turn off the LED.
	 * @returns {Promise<void>} Returns a promise that resolves when the color is successfully set.
	 * @throws {Error} If the color is not a valid hexadecimal string or is not a string at all.
	 * @throws {Error} If the LED cannot be controlled by the device.
	 * @throws {Error} If any other error occurs while setting the color.
	 * @since 4.4.0
	 *
	 * @example
	 * // Set the LED color to red
	 * await sos.hardware.led.setColor('#FF0000');
	 * // Turn off the LED
	 * await sos.hardware.led.setColor(null);
	 */
	public async setColor(color: string | null): Promise<void> {
		this.checkColorValidity(color);
		await this.postMessage({
			type: this.getMessage('set_color'),
			color: color ? color.toUpperCase() : null,
		});
	}

	private checkColorValidity(color: string | null) {
		validate({ color }).canBeNull().string(null, ErrorCodes.HARDWARE_LED_COLOR_IS_NOT_STRING);
		validate({ color })
			.canBeNull()
			.matchRegExp(/^#[0-9A-Fa-f]{6,6}$/, ErrorCodes.HARDWARE_LED_COLOR_IS_NOT_VALID);
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + Led.MESSAGE_PREFIX + '.' + name;
	}
}
